import { Injectable, signal } from "@angular/core";
import { EntityState } from "../_models/IChangedEntity";
import { EntityChangePayload, EntitiesChangedMessage, ParentInfo, PropertyChange } from "../_models/SignalR.models";
import { SignalRNotificationService } from "./SignalRNotificationService";

export interface ChangeEntry {
  tenantId: string;
  //namespace: string;
  entityName: string;
  id: string;
  payload: EntityChangePayload;
  modifiedAt: Date;
  isDerived?: boolean; // true si généré depuis parentInfo
}

@Injectable({ providedIn: 'root' })
export class EntityChangeLogService {
  private readonly MAX_ENTRIES = 500;
  private changeLog = new Map<string, ChangeEntry>();  // key = `${tenantId}:${entityName}:${id}`
  private subscribedTypes = new Set<string>();
  public readonly lastClearedAt = signal<Date | null>(null);

  constructor(private signalR: SignalRNotificationService) {
    this.signalR.messages$.subscribe(msg => {
      this.ingest(msg);
    });
  }

  registerEntityType(entityName: string): void {
    if (this.subscribedTypes.has(entityName)) return;
    this.subscribedTypes.add(entityName);
    this.signalR.subscribeToEntity(entityName);
  }

  getChangesSince(tenantId: string, entityName: string, since: Date | null): ChangeEntry[] {
    const prefix = `${tenantId}:${entityName}:`;
    const prefixedKeys = Array.from(this.changeLog.keys()).filter((key) => { return key.startsWith(prefix) });

    if (!prefixedKeys || prefixedKeys.length == 0)
      return[]; 

    const result: ChangeEntry[] = [];
    const sinceTime = since ? since.getTime() : 0;

    const prefixedEntries = prefixedKeys.map((key: string) => { return this.changeLog.get(key) });
    const targettedEntries = prefixedEntries.filter((entry) => { return entry?.modifiedAt.getTime()||0 > sinceTime }); 

    if (!targettedEntries || targettedEntries.length == 0) {
      return [];
    }
        
    targettedEntries.map((entry) => {  if (entry) {  result.push(entry)  }  });

    let sortedResult = result.sort((a, b) => a.modifiedAt.getTime() - b.modifiedAt.getTime());
    return sortedResult; 
  }

  private ingest(msg: EntitiesChangedMessage): void {
    const tenantId = msg.initiators?.['clientId'];
    if (!tenantId) {
      console.warn('[ChangeLog] Message WebSocket sans clientId, ignoré');
      return;
    }
    for (const [entityName, changes] of Object.entries(msg.entities)) {
      for (const change of changes) {
        this.storeDelta(tenantId, entityName, change);
      }
    }
    this.checkThreshold();
  }

  private storeDelta(tenantId: string, entityName: string, change: EntityChangePayload): void {
    const id = String(change.entity.id ?? change.entity.Id);
    const modifiedAt = this.extractModifiedAt(change);
    const key = `${tenantId}:${entityName}:${id}`;
    this.changeLog.set(key, {
      tenantId,
      entityName,
      id,
      payload: change,
      modifiedAt,
      isDerived: false
    });

    if (change.parentInfo) {
      this.storeDerivedParentDelta(tenantId, change.parentInfo, change, modifiedAt);
    }
  }

  private storeDerivedParentDelta(
    tenantId: string,
    parentInfo: ParentInfo,
    originalChange: EntityChangePayload,
    modifiedAt: Date
  ): void {
    const parentId = String(parentInfo.parentEntityId);
    const parentKey = `${tenantId}:${parentInfo.parentEntityName}:${parentId}`;

    const derivedPropertyChange: PropertyChange = {
      propertyName: parentInfo.parentCollectionName || parentInfo.parentForeignKey || 'related',
      oldValue: null,
      newValue: originalChange.state === EntityState.Deleted ? null : originalChange.entity
    };

    const existing = this.changeLog.get(parentKey);
    if (existing && !existing.isDerived) {
      if (!existing.payload.propertyChanges) existing.payload.propertyChanges = [];
      existing.payload.propertyChanges.push(derivedPropertyChange);
      existing.modifiedAt = modifiedAt;
      return;
    }

    const derivedPayload: EntityChangePayload = {
      entity: { id: parentInfo.parentEntityId },
      state: EntityState.Modified,
      propertyChanges: [derivedPropertyChange],
      modifiedBy: originalChange.modifiedBy,
      modifiedAt: originalChange.modifiedAt
    };

    this.changeLog.set(parentKey, {
      tenantId,
      entityName: parentInfo.parentEntityName,
      id: parentId,
      payload: derivedPayload,
      modifiedAt,
      isDerived: true
    });
  }


  clear(tenantId?: string): void {
    if (tenantId) {
      for (const [key] of this.changeLog) {
        if (key.startsWith(`${tenantId}:`)) {
          this.changeLog.delete(key);
        }
      }
    } else {
      this.changeLog.clear();
    }
    this.lastClearedAt.set(new Date());
  }

  private extractModifiedAt(change: EntityChangePayload): Date {
    if (change.modifiedAt) return new Date(change.modifiedAt);
    const entity = change.entity as any;
    if (entity?.lastModifiedAt) return new Date(entity.lastModifiedAt);
    if (entity?.modifiedAt) return new Date(entity.modifiedAt);
    return new Date();
  }


  private checkThreshold(): void {
    if (this.changeLog.size > this.MAX_ENTRIES) this.clear();
  }
}
